import type { Path, PathValue, UseFormReturn } from 'react-hook-form';
import {
	checkFileQuery,
	uploadFileQuery,
	useAxiosInstance,
	usePoQueries,
} from '@gov-nx/api';
import { propEq, Time } from '@gov-nx/core/types';
import { isAllUploaded } from '@gov-nx/utils/common';
import type {
	FileUpload,
	FileUploadItem,
	FileUploadItemWeb,
	FileUploadProps,
} from './fileUpload.types';
import { FileState, isFileUploadItemWeb } from './fileUpload.types';
import {
	isFileUploaded,
	isFileUploading,
	isFileValidating,
	markFileHasVirus,
	markFileOK,
	markFileUploaded,
	useFileUtils,
} from './fileUploadUtils';

export const hasFileId = (
	file: FileUploadItem
): file is FileUploadItem & Required<Pick<FileUploadItem, 'fileId'>> =>
	file.fileId !== undefined;

export const hasNoSuccessFile = (files: FileUploadItem[]) =>
	!!files.find((file) => file.state !== FileState.success);

export interface FileUploadHook {
	files: FileUploadItem[];
	filesWeb: FileUploadItemWeb[];
	addFiles: (newFiles: FileUpload[]) => void;
	removeFile: (id: string) => void;
	options: FileUploadProps;
	hasNotFinishedUploads: boolean;
	filesUploading: boolean;
	filesValidating: boolean;
	filesUploaded: boolean;
}

export const useFileUpload = <Form extends object>(
	formMethods: UseFormReturn<Form>,
	name: Path<Form>,
	options: FileUploadProps
): FileUploadHook => {
	const getCurrentFiles = () =>
		formMethods.watch(name) ?? ([] as FileUploadItem[]);
	const files = getCurrentFiles();
	const { axiosInstance } = useAxiosInstance();

	const setFiles = (newFiles: FileUploadItem[]) =>
		formMethods.setValue(name, newFiles as PathValue<Form, Path<Form>>, {
			shouldValidate: true,
		});

	const updateFile = (updated: FileUploadItem) =>
		setFiles(
			getCurrentFiles().map((file) => (file.id === updated.id ? updated : file))
		);

	const { validate, toFileUploadItem } = useFileUtils(options);

	usePoQueries({
		queries: files.filter(hasFileId).map((file) => ({
			queryKey: ['file-check', file.id],
			refetchInterval: (file.state === FileState.validating
				? Time['2 seconds']
				: false) as number | false,
			enabled: file.state === FileState.validating,
			queryFn: async () => {
				const response = await checkFileQuery(axiosInstance, file.fileId);
				if (response.stavKontroly === 'OK') {
					const updated = markFileOK(file);
					await finalFileUpdate(updated);
				} else if (response.stavKontroly === 'INFIKOVANY') {
					const updated = markFileHasVirus(file);
					await finalFileUpdate(updated);
				}
				return response;
			},
			onError: () =>
				finalFileUpdate({
					...file,
					state: FileState.error,
					showLargeFileLoader: false,
					message: 'nahrani-souboru.status.chyba-antivirus',
				}),
		})),
	});

	usePoQueries({
		queries: files.map((file) => ({
			queryKey: ['file-upload', file.id],
			enabled: file.state === FileState.uploading,
			queryFn: async () => {
				const response = await uploadFileQuery(axiosInstance, file);
				const updated = markFileUploaded(response, file);
				updateFile(updated);

				return response;
			},
			onError: () =>
				finalFileUpdate({
					...file,
					state: FileState.error,
					showLargeFileLoader: false,
					message: 'nahrani-souboru.status.chyba-nahravani',
				}),
		})),
	});

	const finalFileUpdate = async (file: FileUploadItem) => {
		const updated = files.map((item) => (item.id === file.id ? file : item));
		if (options.onAllFilesUploaded && isAllUploaded(updated)) {
			const uploaded = await options.onAllFilesUploaded(updated);
			setFiles(uploaded);
		} else {
			updateFile(file);
		}
	};

	const addFiles = (newFiles: FileUpload[]) => {
		const newUploads = newFiles
			.filter((govFile) => !files.find((file) => file.id === govFile.id))
			.map(toFileUploadItem(files.length));

		setFiles([...files, ...newUploads].map(validate));
	};

	const removeFile = (id: string) => {
		setFiles(files.filter((file) => file.id !== id).map(validate));

		if (options.onFileRemoved) {
			const existing = files.find(propEq('id', id));

			existing && options.onFileRemoved(existing);
		}
	};

	const hasNotFinishedUploads = hasNoSuccessFile(files);

	return {
		files,
		filesWeb: files.filter(isFileUploadItemWeb),
		addFiles,
		removeFile,
		options,
		hasNotFinishedUploads,
		filesUploading: files.some(isFileUploading),
		filesValidating: files.some(isFileValidating),
		filesUploaded: files.every(isFileUploaded) && files.length > 0,
	};
};
